<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import ManufacturersAPI from 'dashboard/api/manufacturers';
import Drawer from './Drawer.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Textarea from 'dashboard/components-next/textarea/TextArea.vue';

const props = defineProps({
  manufacturer: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['close', 'save']);
const { t } = useI18n();

const loading = ref(false);
const logo = ref(null);
const logoFile = ref(null);

const formData = ref({
  name: '',
  description: '',
  website: '',
});

const isEdit = computed(() => !!props.manufacturer);
const modalTitle = computed(() =>
  isEdit.value
    ? t('WEBSITE.MANUFACTURERS.FORM.EDIT')
    : t('WEBSITE.MANUFACTURERS.FORM.NEW_MANUFACTURER')
);

onMounted(() => {
  if (isEdit.value && props.manufacturer) {
    formData.value = {
      name: props.manufacturer.name || '',
      description: props.manufacturer.description || '',
      website: props.manufacturer.website || '',
    };

    // Charger le logo existant
    if (props.manufacturer.logo) {
      logo.value = props.manufacturer.logo;
    }
  }
});

const handleLogoChange = event => {
  const file = event.target.files[0];
  if (file) {
    if (!file.type.startsWith('image/')) {
      useAlert(t('WEBSITE.MANUFACTURERS.FORM.LOGO_MUST_BE_IMAGE'));
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      useAlert(t('WEBSITE.MANUFACTURERS.FORM.LOGO_TOO_LARGE'));
      return;
    }
    logoFile.value = file;
    // Créer une URL de prévisualisation
    const reader = new FileReader();
    reader.onload = e => {
      logo.value = {
        url: e.target.result,
        filename: file.name,
        byte_size: file.size,
        content_type: file.type,
      };
    };
    reader.readAsDataURL(file);
  }
};

const removeLogo = () => {
  logo.value = null;
  logoFile.value = null;
};

const handleSubmit = async () => {
  if (!formData.value.name.trim()) {
    useAlert(t('WEBSITE.MANUFACTURERS.FORM.NAME_REQUIRED'));
    return;
  }

  loading.value = true;

  try {
    const hasNewLogo = logoFile.value !== null;

    let payload;

    if (hasNewLogo) {
      // Utiliser FormData si on a un nouveau logo
      payload = new FormData();

      Object.keys(formData.value).forEach(key => {
        const value = formData.value[key];
        if (value !== null && value !== undefined && value !== '') {
          payload.append(`manufacturer[${key}]`, value);
        }
      });

      payload.append('manufacturer[logo]', logoFile.value);
    } else {
      // Utiliser JSON si pas de nouveau logo
      payload = {
        manufacturer: {
          ...formData.value,
        },
      };
    }

    if (isEdit.value) {
      await ManufacturersAPI.update(props.manufacturer.id, payload);
      useAlert(t('WEBSITE.MANUFACTURERS.API.SUCCESS.MANUFACTURER_UPDATED'));
    } else {
      await ManufacturersAPI.create(payload);
      useAlert(t('WEBSITE.MANUFACTURERS.API.SUCCESS.MANUFACTURER_CREATED'));
    }

    emit('save');
  } catch (error) {
    // Erreur déjà gérée par useAlert dans le bloc if/else ci-dessus
    let errorMessage = isEdit.value
      ? t('WEBSITE.MANUFACTURERS.API.ERROR.UPDATE_FAILED')
      : t('WEBSITE.MANUFACTURERS.API.ERROR.CREATE_FAILED');

    // Afficher les erreurs détaillées du serveur si disponibles
    if (error.response?.data?.errors) {
      const errors = Array.isArray(error.response.data.errors)
        ? error.response.data.errors.join(', ')
        : Object.values(error.response.data.errors).flat().join(', ');
      errorMessage = `${errorMessage}: ${errors}`;
    } else if (error.response?.data?.error) {
      errorMessage = `${errorMessage}: ${error.response.data.error}`;
    }

    useAlert(errorMessage);
  } finally {
    loading.value = false;
  }
};

const handleClose = () => {
  emit('close');
};
</script>

<template>
  <Drawer show size="md" @close="handleClose">
    <!-- Header -->
    <div
      class="flex items-center justify-between px-6 py-4 border-b border-n-weak"
    >
      <h2 class="text-lg font-semibold text-n-slate-12">
        {{ modalTitle }}
      </h2>
      <button
        class="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-n-alpha-2 text-n-slate-11 transition-colors"
        @click="handleClose"
      >
        <span class="i-lucide-x size-5" />
      </button>
    </div>

    <!-- Content -->
    <div class="flex-1 overflow-y-auto px-6 py-6">
      <div class="space-y-6">
        <!-- Logo -->
        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-2">
            {{ t('WEBSITE.MANUFACTURERS.FORM.LOGO') }}
          </label>
          <div class="border border-n-weak rounded-lg p-4 bg-n-alpha-1">
            <!-- Affichage du logo existant ou nouveau -->
            <div
              v-if="logo"
              class="flex items-center justify-between p-3 bg-white border border-n-weak rounded-lg"
            >
              <div class="flex items-center gap-3">
                <img
                  :src="logo.url"
                  :alt="formData.name"
                  class="w-16 h-16 object-cover rounded-lg border border-n-weak"
                />
                <div>
                  <p class="text-sm font-medium text-n-slate-12">
                    {{ logo.filename || t('WEBSITE.MANUFACTURERS.FORM.LOGO') }}
                  </p>
                  <p class="text-xs text-n-slate-10">
                    {{
                      logoFile
                        ? (logoFile.size / 1024).toFixed(2) + ' KB'
                        : (logo.byte_size / 1024).toFixed(2) + ' KB'
                    }}
                  </p>
                </div>
              </div>
              <div class="flex items-center gap-2">
                <button
                  type="button"
                  class="p-2 hover:bg-n-alpha-2 rounded-lg transition-colors"
                  :title="t('WEBSITE.MANUFACTURERS.FORM.LOGO_REMOVE')"
                  @click="removeLogo"
                >
                  <span class="i-lucide-trash-2 size-4 text-n-red-9" />
                </button>
              </div>
            </div>

            <!-- Zone d'upload -->
            <div v-else>
              <label
                class="flex flex-col items-center justify-center p-6 border-2 border-dashed border-n-weak rounded-lg cursor-pointer hover:border-n-blue-9 hover:bg-n-alpha-2 transition-colors"
              >
                <span class="i-lucide-upload size-8 text-n-slate-10 mb-2" />
                <span class="text-sm font-medium text-n-slate-12 mb-1">
                  {{ t('WEBSITE.MANUFACTURERS.FORM.LOGO_UPLOAD') }}
                </span>
                <span class="text-xs text-n-slate-10">
                  {{ t('WEBSITE.MANUFACTURERS.FORM.LOGO_FORMAT') }}
                </span>
                <input
                  type="file"
                  accept="image/*"
                  class="hidden"
                  @change="handleLogoChange"
                />
              </label>
            </div>
          </div>
        </div>

        <!-- Nom du fabricant -->
        <div>
          <label
            class="block text-sm font-medium text-n-slate-12 mb-1.5"
            for="manufacturer-name"
          >
            {{ t('WEBSITE.MANUFACTURERS.FORM.NAME') }}
            <span class="text-red-500" aria-hidden="true">*</span>
          </label>
          <Input
            id="manufacturer-name"
            v-model="formData.name"
            :placeholder="t('WEBSITE.MANUFACTURERS.FORM.NAME_PLACEHOLDER')"
            required
          />
        </div>

        <!-- Description -->
        <div>
          <label
            class="block text-sm font-medium text-n-slate-12 mb-1.5"
            for="manufacturer-description"
          >
            {{ t('WEBSITE.MANUFACTURERS.FORM.DESCRIPTION') }}
          </label>
          <Textarea
            id="manufacturer-description"
            v-model="formData.description"
            :placeholder="
              t('WEBSITE.MANUFACTURERS.FORM.DESCRIPTION_PLACEHOLDER')
            "
            :rows="4"
          />
        </div>

        <!-- Website -->
        <div>
          <label
            class="block text-sm font-medium text-n-slate-12 mb-1.5"
            for="manufacturer-website"
          >
            {{ t('WEBSITE.MANUFACTURERS.FORM.WEBSITE') }}
          </label>
          <Input
            id="manufacturer-website"
            v-model="formData.website"
            type="url"
            :placeholder="t('WEBSITE.MANUFACTURERS.FORM.WEBSITE_PLACEHOLDER')"
          />
        </div>
      </div>
    </div>

    <!-- Footer -->
    <div
      class="flex items-center justify-end gap-3 px-6 py-4 border-t border-n-weak"
    >
      <Button
        :label="t('WEBSITE.MANUFACTURERS.FORM.CANCEL')"
        variant="outline"
        color="slate"
        @click="handleClose"
      />
      <Button
        :label="
          isEdit
            ? t('WEBSITE.MANUFACTURERS.FORM.SAVE')
            : t('WEBSITE.MANUFACTURERS.FORM.CREATE')
        "
        :loading="loading"
        @click="handleSubmit"
      />
    </div>
  </Drawer>
</template>
